import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ButtonGroup, Divider, Stack, Typography } from '@mui/material';

import TextInput from 'components/Common/TextInput';
import CardWrapper from 'components/Common/CardWrapper';
import CustomButton from 'components/Common/CustomButton';
import SearchItem from 'components/Common/SearchItem';
import NoResults from 'components/Common/NoResultsText';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { camelToSpacedPascalCase, debounce, getFullName } from 'utils/helpers';
import { fetchMerchantUser, fetchPersonalUser } from 'services/user/fetchUserEntities';
import routes from 'constants/routes';
import { MERCHANT_TYPE, PERSONAL_TYPE, SEND_REQUEST, SEND_MONEY } from 'types/sendAndRequest';
import { getRecentFrequentUsers } from 'redux/slices/sendAndRequest/sendMoneyActivitySlice';
import AutocompleteOption from 'components/Common/autocomplete/AutocompleteOption';
import List from 'components/Common/List';
import defaultBusinessImage from '../../assets/business.jpeg';
import defaultPersonalImage from '../../assets/personal.jpeg';

export interface Row {
    companyId: number;
    customerId?: number;
}
interface SearchBusinessAndUserPropTypes {
    title: typeof SEND_REQUEST | typeof SEND_MONEY;
}
const SearchBusinessAndUser: React.FC<SearchBusinessAndUserPropTypes> = ({ title }) => {
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [selectButton, setSelectButton] = useState('personal');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [recentAndFrequentLoading, setRecentAndFrequentLoading] = useState(false);
    const [recents, setRecents] = useState([]);
    const [frequents, setFrequents] = useState([]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        if (selectButton === 'personal') {
            const userList = await fetchPersonalUser(debouncedSearchText);
            setData(userList);
        } else {
            const companyList = await fetchMerchantUser(debouncedSearchText);
            setData(companyList);
        }
        setLoading(false);
    }, [dispatch, debouncedSearchText, selectButton]);

    useEffect(() => {
        (async () => {
            setRecentAndFrequentLoading(true);
            const { payload } = await dispatch(getRecentFrequentUsers());
            setRecentAndFrequentLoading(false);
            if (payload) {
                const { recents = [], frequents = [] } = payload || {};
                setRecents(recents);
                setFrequents(frequents);
            }
        })();
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData, debouncedSearchText, selectButton]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSetSearchText = useCallback(debounce(setDebouncedSearchText, 700), []);

    useEffect(() => {
        debouncedSetSearchText(searchText);
    }, [searchText, debouncedSetSearchText]);

    const handleSearch = (e: any) => {
        const { value } = e.target || {};
        setSearchText(value);
    };

    const navigationHandler = (id: number, data?: any) => {
        const idString = id?.toString();

        if (title === SEND_MONEY) {
            const state = { ...data, receiverType: data?.defaultAccount ? MERCHANT_TYPE : PERSONAL_TYPE };
            navigate(routes.sendAndRequest.sendMoneyForm.replace(':id', idString), { state });
        } else if (title === SEND_REQUEST) {
            const invoiceNumber = data?.customerId || data?.defaultAccount || '';
            navigate(routes.sendAndRequest.requestMoneyForm.replace(':id', idString), {
                state: { invoiceNumber, ...data },
            });
        }
    };

    const handleAccountTypeClick = (type: string) => {
        setSelectButton(type);
        setSearchText('');
    };
    const handleRecentOrFrequentItemClick = (item: any) => {
        const state = {
            ...item,
            receiverType: selectButton === 'personal' ? PERSONAL_TYPE : MERCHANT_TYPE,
        };
        if (title === SEND_MONEY) {
            navigate(routes.sendAndRequest.sendMoneyForm.replace(':id', item.nickname), { state });
        } else if (title === SEND_REQUEST) {
            navigate(routes.sendAndRequest.requestMoneyForm.replace(':id', item.nickname), { state });
        }
    };
    const renderList = (data: any) => {
        const style = { height: '200px' };
        const flexContainerProps = { style };
        const filteredBusiness = data.filter((i: any) => i?.isMerchant);
        const filteredUsers = data.filter((i: any) => !i?.isMerchant);
        return (
            <List
                data={selectButton === 'personal' ? filteredUsers : filteredBusiness}
                showLoading
                loading={recentAndFrequentLoading}
                noResultProps={{ style }}
                loaderProps={{ flexContainerProps }}
            >
                {(item: any) => {
                    const { firstName = '', lastName = '', nickname, imgUrl } = item || {};
                    const option = {
                        name: (firstName + ' ' + lastName)?.trim(),
                        email: nickname,
                        imgUrl: imgUrl,
                        fallbackImage: selectButton === 'personal' ? defaultPersonalImage : defaultBusinessImage,
                    };
                    return (
                        <Stack mb={1}>
                            <AutocompleteOption
                                props={{
                                    onClick: () => handleRecentOrFrequentItemClick(item),
                                    sx: {
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: '#f8f2f2',
                                        },
                                        boxShadow: 'rgba(0, 0, 0, 0.15) 0px 1px 1.3px',
                                    },
                                }}
                                file
                                option={option}
                            />
                        </Stack>
                    );
                }}
            </List>
        );
    };
    return (
        <>
            <CardWrapper>
                <Typography
                    sx={{
                        fontSize: 20,
                        mb: 2,
                        fontWeight: 400,
                        textTransform: 'capitalize',
                    }}
                    variant="h1"
                >
                    {camelToSpacedPascalCase(title)}
                </Typography>
                <Divider />
                <ButtonGroup sx={{ mt: 2 }}>
                    <CustomButton
                        variant={selectButton === 'personal' ? 'contained' : 'outlined'}
                        title="Personal User"
                        sx={{ width: '50%' }}
                        onClick={() => handleAccountTypeClick('personal')}
                    />
                    <CustomButton
                        variant={selectButton === 'business' ? 'contained' : 'outlined'}
                        title="Business User"
                        sx={{ width: '50%' }}
                        onClick={() => handleAccountTypeClick('business')}
                    />
                </ButtonGroup>
                <Typography sx={{ mt: 2, mb: 4 }} variant="body1">
                    Use the list or search bar to choose the business or person you would like to {camelToSpacedPascalCase(title)} to.
                </Typography>
                <TextInput value={searchText} sx={{ mb: 4 }} width="100%" placeholder="Search here" onChange={handleSearch} />
                {debouncedSearchText && !loading && !!data?.length && (
                    <Stack gap={2}>
                        {(data || []).map((item: any, index) => {
                            const { imgUrl, logoUrl, firstName, lastName, name, nickname, defaultAccount, customerId } = item || {};
                            return (
                                <SearchItem
                                    key={index}
                                    onClick={() => navigationHandler(defaultAccount || customerId, item)}
                                    name={name || getFullName({ firstName, lastName })}
                                    nickName={nickname || 'Dummy NickName'}
                                    src={imgUrl || logoUrl}
                                />
                            );
                        })}
                    </Stack>
                )}
                {debouncedSearchText && !loading && !data?.length && <NoResults height="300px" />}
                {debouncedSearchText && loading && <NoResults height="300px" message="Loading . . ." />}
                {!debouncedSearchText && (
                    // <Stack justifyContent="center" alignItems="center" minHeight="300px">
                    //     <Typography>
                    //         Start typing a name, email or nickname into the search bar above and the results will show here.
                    //     </Typography>
                    // </Stack>
                    <Stack minHeight="300px" gap={4}>
                        <Stack spacing={2}>
                            <Typography fontSize={18} fontWeight={600}>
                                Recents
                            </Typography>
                            {renderList(recents)}
                        </Stack>
                        <Stack spacing={2}>
                            <Typography fontSize={18} fontWeight={600}>
                                Frequents
                            </Typography>
                            {renderList(frequents)}
                        </Stack>
                    </Stack>
                )}
                {/* {title === "send request" && (
            <CustomButton
              title="View QR Code "
              sx={{ width: "30%", position: "relative", left: "35%" }}
              onClick={() =>
                navigate(routes.qr.main, { state: { showButton: true } })
              }
            />
          )} */}
            </CardWrapper>
        </>
    );
};
export default SearchBusinessAndUser;
